import {
  ApplicationQuestion,
  CommonQuestionKeys,
  Field,
  FieldType,
  KCCustomQuestionKeys,
  KCSMBLoanPrepCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { eq, inList } from '../../../ast/utils.js';
import { CREDIT_RANGES, ETHNICITIES } from '../options.js';

type ExtractedCommonQuestionKeys = Extract<
  CommonQuestionKeys,
  | CommonQuestionKeys.AFFIRMATION
  | CommonQuestionKeys.MAILING_ADDRESS
  | CommonQuestionKeys.PHONE_NUMBER
  | CommonQuestionKeys.EMAIL
  | CommonQuestionKeys.ETHNICITY
  | CommonQuestionKeys.GENDER
>;
type ExtractedKCCommonQuestionKeys = Extract<
  KCCustomQuestionKeys,
  | KCCustomQuestionKeys.COMPANY_LEGAL_NAME
  | KCCustomQuestionKeys.BUSINESS_ADDRESS
  | KCCustomQuestionKeys.CREDIT_SCORE
>;

export const QUESTIONS: {
  [key in
    | ExtractedCommonQuestionKeys
    | ExtractedKCCommonQuestionKeys
    | KCSMBLoanPrepCustomQuestionKeys]: ApplicationQuestion;
} = {
  [KCCustomQuestionKeys.COMPANY_LEGAL_NAME]: {
    key: KCCustomQuestionKeys.COMPANY_LEGAL_NAME,
    copy: { title: 'Business Legal Name' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Business Legal Name',
        key: KCCustomQuestionKeys.COMPANY_LEGAL_NAME,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.OTHER_RELATED_BUSINESSES]: {
    key: KCSMBLoanPrepCustomQuestionKeys.OTHER_RELATED_BUSINESSES,
    copy: {
      title: 'Other Related Business Entities',
    },
    fields: [
      {
        type: FieldType.Text,
        key: `${KCSMBLoanPrepCustomQuestionKeys.OTHER_RELATED_BUSINESSES}.entities`,
        inputType: TextInputType.Text,
        copy: 'Other Related Business Entities',
        props: {
          multiline: true,
          rows: 5,
        },
      },
    ],
    skippable: true,
  },
  [KCCustomQuestionKeys.BUSINESS_ADDRESS]: {
    key: KCCustomQuestionKeys.BUSINESS_ADDRESS,
    copy: { title: 'Business Mailing Address' },
    fields: [
      {
        type: FieldType.Address,
        key: KCCustomQuestionKeys.BUSINESS_ADDRESS,
        copy: 'Business Mailing Address',
      },
    ],
  },
  [CommonQuestionKeys.MAILING_ADDRESS]: {
    key: CommonQuestionKeys.MAILING_ADDRESS,
    copy: { title: 'Business Address (if different)' },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.MAILING_ADDRESS,
        copy: 'Business Address',
      },
    ],
    skippable: true,
  },
  [KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_NAME]: {
    key: KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_NAME,
    copy: { title: 'Primary Contact Name' },
    fields: [
      {
        type: FieldType.Typography,
        key: `${KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_NAME}.label`,
        copy: 'Please include the first and last name.',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Primary Contact Name',
        key: KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_NAME,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_TITLE]: {
    key: KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_TITLE,
    copy: { title: 'Primary Contact Title' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Primary Contact Title',
        key: KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_TITLE,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.PURPOSE_OF_FINANCING]: {
    key: KCSMBLoanPrepCustomQuestionKeys.PURPOSE_OF_FINANCING,
    copy: { title: 'Primary of Financing' },
    fields: [
      {
        type: FieldType.Typography,
        key: `${KCSMBLoanPrepCustomQuestionKeys.PURPOSE_OF_FINANCING}.label`,
        copy: 'Please be as detailed as possible.',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Purpose of Financing',
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 1000 },
        },
        key: KCSMBLoanPrepCustomQuestionKeys.PRIMARY_CONTACT_TITLE,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.BANK_OR_FINANCIAL_INSTITUTION]: {
    key: KCSMBLoanPrepCustomQuestionKeys.BANK_OR_FINANCIAL_INSTITUTION,
    copy: {
      title:
        'What bank/financial institution are you requesting a loan from/planning on requesting a loan from? ',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Bank/financial institution',
        key: KCSMBLoanPrepCustomQuestionKeys.BANK_OR_FINANCIAL_INSTITUTION,
      },
    ],
  },
  [CommonQuestionKeys.PHONE_NUMBER]: {
    key: CommonQuestionKeys.PHONE_NUMBER,
    copy: { title: 'Primary Contact Phone' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        copy: 'Primary Contact Phone',
        key: CommonQuestionKeys.PHONE_NUMBER,
      },
    ],
  },
  [CommonQuestionKeys.EMAIL]: {
    key: CommonQuestionKeys.EMAIL,
    copy: { title: 'Primary Contact Email' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        copy: 'Primary Contact Email',
        key: CommonQuestionKeys.EMAIL,
      },
    ],
  },
  [CommonQuestionKeys.ETHNICITY]: {
    key: CommonQuestionKeys.ETHNICITY,
    copy: { title: 'Does the primary contact identify as any of the following?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Ethnicity',
        key: 'ethnicity.ethnicity',
        props: { multiple: true },
        options: ETHNICITIES,
      },
      {
        type: FieldType.Typography,
        key: 'ethnicity.other.label',
        copy: 'If the primary contact identifies with an ethnicity not included in the previous question, please list it here.',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'ethnicity.other',
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
        copy: 'Other ethnicity',
      },
    ],
  },
  [CommonQuestionKeys.GENDER]: {
    key: CommonQuestionKeys.GENDER,
    copy: { title: `What is the primary contact's current gender identity?` },
    skippable: true,
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Gender',
        key: 'gender.gender',
        props: { multiple: true },
        validation: { required: false },
        options: [
          { label: 'Man', value: 'man' },
          { label: 'Woman', value: 'woman' },
          { label: 'Cisgender', value: 'cisgender' },
          { label: 'Transgender', value: 'transgender' },
          { label: 'Demigender', value: 'demigender' },
          { label: 'Agender', value: 'agender' },
          { label: 'Two-spirit /Culturally specific third gender', value: 'thirdgender' },
          { label: 'Non-binary', value: 'non-binary' },
          { label: 'Genderqueer', value: 'genderqueer' },
          { label: 'I use a different term (specify)', value: 'other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'gender.otherGender',
        dynamicLogic: inList('gender.gender', 'other'),
        copy: 'Specify',
      },
    ] as Field[],
  },
  [KCCustomQuestionKeys.CREDIT_SCORE]: {
    key: KCCustomQuestionKeys.CREDIT_SCORE,
    copy: { title: "What is the primary contact's personal credit score?" },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Credit score',
        options: CREDIT_RANGES,
        key: 'creditScore.score',
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.OWNER_INFORMATION]: {
    key: KCSMBLoanPrepCustomQuestionKeys.OWNER_INFORMATION,
    copy: { title: 'List any owners of 20% or more of the business' },
    layout: 'panel',
    fields: [
      {
        type: FieldType.Complex,
        key: 'owners',
        copy: 'Owner',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name',
            props: { helperText: 'First and last name' },
            key: 'name',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Number,
            copy: 'Percentage of ownership',
            key: 'percentageOfOwnership',
            props: { inputProps: { maxlength: 3 } },
          },
          {
            type: FieldType.Dropdown,
            copy: 'Gender',
            key: 'gender.gender',
            props: { multiple: true },
            validation: { required: false },
            options: [
              { label: 'Man', value: 'man' },
              { label: 'Woman', value: 'woman' },
              { label: 'Cisgender', value: 'cisgender' },
              { label: 'Transgender', value: 'transgender' },
              { label: 'Demigender', value: 'demigender' },
              { label: 'Agender', value: 'agender' },
              { label: 'Two-spirit /Culturally specific third gender', value: 'thirdgender' },
              { label: 'Non-binary', value: 'non-binary' },
              { label: 'Genderqueer', value: 'genderqueer' },
              { label: 'I use a different term (specify)', value: 'other' },
            ],
          },
          {
            type: FieldType.Typography,
            key: 'gender.otherGender.label',
            copy: 'If the owner identifies with a gender not included in the previous question, please specify here.',
            props: { variant: 'body', alignment: 'left' },
            dynamicLogic: inList('gender.gender', 'other'),
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'gender.otherGender',
            dynamicLogic: inList('gender.gender', 'other'),
            copy: 'Other gender',
          },
          {
            type: FieldType.Dropdown,
            copy: 'Ethnicity',
            key: 'ethnicity.ethnicity',
            props: { multiple: true },
            options: ETHNICITIES,
          },
          {
            type: FieldType.Typography,
            key: 'ethnicity.other.label',
            copy: 'If the owner identifies with an ethnicity not included in the previous question, please list it here.',
            props: { variant: 'body', alignment: 'left' },
            dynamicLogic: inList('ethnicity.ethnicity', 'other'),
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'ethnicity.other',
            dynamicLogic: inList('ethnicity.ethnicity', 'other'),
            copy: 'Other ethnicity',
          },
        ],
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.LOAN_AMOUNT]: {
    key: KCSMBLoanPrepCustomQuestionKeys.LOAN_AMOUNT,
    copy: { title: 'Amount of Loan Requested' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Amount of Loan Requested',
        key: KCSMBLoanPrepCustomQuestionKeys.LOAN_AMOUNT,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.YTD_EXPENSES]: {
    key: KCSMBLoanPrepCustomQuestionKeys.YTD_EXPENSES,
    copy: {
      title: 'Please provide your total business prior YTD expenses',
      intro: 'estimate if needed',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'YTD expenses',
        key: KCSMBLoanPrepCustomQuestionKeys.YTD_EXPENSES,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.PERSONAL_INCOME_AND_EXPENSES]: {
    key: KCSMBLoanPrepCustomQuestionKeys.PERSONAL_INCOME_AND_EXPENSES,
    copy: { title: 'What are your personal annual income and expenses?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Personal annual income',
        key: `${KCSMBLoanPrepCustomQuestionKeys.PERSONAL_INCOME_AND_EXPENSES}.income`,
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Personal annual expenses',
        key: `${KCSMBLoanPrepCustomQuestionKeys.PERSONAL_INCOME_AND_EXPENSES}.expenses`,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.BUSINESS_PAYS_INCOME]: {
    key: KCSMBLoanPrepCustomQuestionKeys.BUSINESS_PAYS_INCOME,
    copy: { title: 'Does your business pay your personal income?' },
    fields: [
      {
        type: FieldType.RadioList,
        options: YesNoOptions,
        key: KCSMBLoanPrepCustomQuestionKeys.BUSINESS_PAYS_INCOME,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.HAVE_ANY_OPEN_LOANS]: {
    key: KCSMBLoanPrepCustomQuestionKeys.HAVE_ANY_OPEN_LOANS,
    copy: { title: 'Do you have an open loan(s) with any financial institution at this time?' },
    layout: 'panel',
    fields: [
      {
        type: FieldType.RadioList,
        options: YesNoOptions,
        key: `${KCSMBLoanPrepCustomQuestionKeys.HAVE_ANY_OPEN_LOANS}.affirmation`,
      },
      {
        type: FieldType.Typography,
        key: 'haveAnyOpenLoans.loans.label',
        copy: '(If yes to the above) Please complete the following:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq(
          `${KCSMBLoanPrepCustomQuestionKeys.HAVE_ANY_OPEN_LOANS}.affirmation`,
          'yes',
        ),
      },
      {
        type: FieldType.Complex,
        key: 'haveAnyOpenLoans.loans',
        copy: 'Loan',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name of financial institution',
            key: 'name',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'Amount of original loan',
            key: 'originalLoan',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'Amount owed on loan',
            key: 'amountOwed',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Number,
            copy: 'Current interest rate',
            key: 'interestRate',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Term of loan',
            key: 'term',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'Monthly loan payment amount',
            key: 'monthlyPayment',
          },
        ],
        dynamicLogic: eq(
          `${KCSMBLoanPrepCustomQuestionKeys.HAVE_ANY_OPEN_LOANS}.affirmation`,
          'yes',
        ),
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION]: {
    key: KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION,
    copy: {
      title:
        'Please enter your Financial Information for the last fiscal year listed on the Federal Business Tax Returns',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Gross Sales',
        key: `${KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION}.grossSales`,
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Net Income',
        key: `${KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION}.netIncome`,
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Total Operating Expenses',
        key: `${KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION}.totalOperatingExpenses`,
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Monthly Payroll Expenses',
        key: `${KCSMBLoanPrepCustomQuestionKeys.FISCAL_YEAR_FINANCIAL_INFORMATION}.monthlyPayrollExpenses`,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.SUPPORTING_DOCUMENTS]: {
    key: KCSMBLoanPrepCustomQuestionKeys.SUPPORTING_DOCUMENTS,
    copy: {
      title: 'Please upload the following documents to your application',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: `${KCSMBLoanPrepCustomQuestionKeys.SUPPORTING_DOCUMENTS}.label`,
        props: { variant: 'body', alignment: 'center' },
        copy: 'All documents are mandatory. Any missing documents will result in an incomplete application. Please note the documents requested below are initial documentation needed to move forward in the process. Additional documentation will be required.',
      },
      {
        type: FieldType.Typography,
        key: `${KCSMBLoanPrepCustomQuestionKeys.SUPPORTING_DOCUMENTS}.items`,
        props: { variant: 'largeBody', alignment: 'center' },
        copy: `
&#x2022; 2024 KCMO Business License

&#x2022; Past Two Years of Tax Returns
 
&#x2022; Past Two Years of Financials: Profit/Loss and Balance Sheet
        `,
      },
      {
        type: FieldType.Document,
        key: KCSMBLoanPrepCustomQuestionKeys.SUPPORTING_DOCUMENTS,
      },
    ],
  },
  [KCSMBLoanPrepCustomQuestionKeys.CAN_COMMIT_TO_STATUS_UPDATES]: {
    key: KCSMBLoanPrepCustomQuestionKeys.CAN_COMMIT_TO_STATUS_UPDATES,
    copy: {
      title:
        'Upon completing the Loan Preparation Program, you may be required to report on the status of your loan application for up to one (1) year. Do you acknowledge this requirement?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: `${KCSMBLoanPrepCustomQuestionKeys.CAN_COMMIT_TO_STATUS_UPDATES}.affirmation`,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: { title: 'Please confirm this statement:' },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: 'I affirm the above to be true and accurate.  I understand that financial statements, references, an interview and/or site visit may be necessary for the purposes of selecting loan preparation program participants.',
      },
    ],
  },
};
